import React, { useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { formatDecimal } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';
import type { CustomerFinishedGood } from '../../../services/report.service';

interface CustomerFgHistoryProps {
  fg: CustomerFinishedGood;
  onBack: () => void;
  onOpenOrder: (orderId: string) => void;
}

// Local, TZ-safe date label (avoids new Date('YYYY-MM-DD') UTC shift).
const formatDateLabel = (iso: string): string => {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric' }).format(
    new Date(y, m - 1, d)
  );
};

export const CustomerFgHistory: React.FC<CustomerFgHistoryProps> = ({ fg, onBack, onOpenOrder }) => {
  const { t } = useLanguage();
  const total = useMemo(() => fg.purchases.reduce((sum, p) => sum + p.qty, 0), [fg.purchases]);

  return (
    <div data-test-id="customer-fg-history">
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          data-test-id="customer-fg-history-back"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4" /> {t('reports.customer.history.back')}
        </button>
        <span className="text-sm font-semibold text-gray-900">{fg.name}</span>
      </div>

      {fg.purchases.length === 0 ? (
        <p className="text-sm text-gray-500">{t('reports.customer.history.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="px-3 py-2 font-medium">{t('reports.customer.history.date')}</th>
                <th className="px-3 py-2 font-medium">{t('reports.customer.history.invoice')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('reports.customer.history.qty')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fg.purchases.map((p, i) => (
                <tr key={`${p.orderId}-${i}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-700">{formatDateLabel(p.date)}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onOpenOrder(p.orderId)}
                      data-test-id={`customer-fg-history-order-${p.orderId}`}
                      className="font-medium text-primary-600 hover:underline"
                    >
                      {p.orderNumber}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900">{formatDecimal(p.qty, 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 font-semibold">
                <td className="px-3 py-2 text-gray-900" colSpan={2}>
                  {t('reports.customer.history.total')}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-900">{formatDecimal(total, 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
